import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GraduationCap, CheckCircle2, ArrowUpRight, LogOut, Wrench, Download, Award, LayoutDashboard, FileBarChart, User, RotateCcw, ShieldCheck, BookLock, Loader2, ExternalLink } from "lucide-react";
import { FORMATIONS } from "../data/content";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BY_LOOKUP = Object.fromEntries(FORMATIONS.map((f) => [f.lookup, f]));

const STATUS_LABEL = { non_commencee: "Non commencée", en_cours: "En cours", terminee: "Terminée" };
const STATUS_STYLE = {
  non_commencee: "text-ink/50 bg-ink/5 border-ink/10",
  en_cours: "text-blue-700 bg-blue-50 border-blue-200",
  terminee: "text-gold bg-gold/10 border-gold/40",
  reussie: "text-green-700 bg-green-50 border-green-200",
};

const TABS = [
  ["dashboard", "Tableau de bord", LayoutDashboard],
  ["formations", "Mes formations", GraduationCap],
  ["resultats", "Mes résultats", FileBarChart],
  ["certificats", "Mes certificats", Award],
  ["outils", "Mes outils", Wrench],
  ["profil", "Mon profil", User],
];

export default function MonEspace() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [owned, setOwned] = useState([]);
  const [progress, setProgress] = useState([]);
  const [results, setResults] = useState([]);
  const [certs, setCerts] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [f, p, r, c, t] = await Promise.all([
          axios.get(`${API}/me/formations`),
          axios.get(`${API}/lms/progress`),
          axios.get(`${API}/lms/results`),
          axios.get(`${API}/me/certificates`),
          axios.get(`${API}/me/tools`),
        ]);
        setOwned(f.data.formations || []);
        setProgress(p.data || []);
        setResults(r.data || []);
        setCerts(c.data || []);
        setTools(t.data || []);
      } catch (e) { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const certByLookup = useMemo(() => Object.fromEntries(certs.filter((c) => c.status === "VALID").map((c) => [c.formation_lookup, c])), [certs]);

  const stats = useMemo(() => {
    const started = progress.filter((p) => p.percent > 0 && p.percent < 100).length;
    const done = progress.filter((p) => p.percent >= 100).length;
    const global = progress.length ? Math.round(progress.reduce((s, p) => s + p.percent, 0) / progress.length) : 0;
    return { total: owned.length, started, done, certs: certs.length, global };
  }, [owned, progress, certs]);

  const continueFormation = useMemo(() => {
    const inProgress = progress.filter((p) => p.percent > 0 && p.percent < 100)
      .sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
    return inProgress[0] || progress.find((p) => p.percent === 0) || null;
  }, [progress]);

  const downloadTool = async (t) => {
    try {
      const res = await axios.get(`${API}/tools/${t.slug}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${t.name}.${t.file_ext || "zip"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const viewTool = async (t) => {
    const win = window.open("", "_blank");
    try {
      const res = await axios.get(`${API}/tools/${t.slug}/view`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      if (win) { win.location.href = url; } else { window.open(url, "_blank"); }
    } catch (e) { win?.close(); toast.error("Ouverture impossible."); }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <>
      <section className="bg-ink-900 text-paper grain relative pt-32 pb-14 md:pt-40 overflow-hidden" data-testid="mon-espace">
        <div className="absolute right-0 top-0 w-[420px] h-[420px] rounded-full bg-gold/10 blur-[110px]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="label-mono text-gold">NovaLean Learning</span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl font-semibold mt-4 tracking-tight leading-[0.95]">
              Bonjour {user?.name?.split(" ")[0] || ""},
            </motion.h1>
            <p className="text-paper/55 mt-3">Bienvenue dans votre espace personnel.</p>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-5 py-3 label-mono hover:bg-paper hover:text-ink transition-colors w-fit">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </section>

      <section className="bg-paper py-12 md:py-16 min-h-[50vh]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex gap-2 mb-10 overflow-x-auto pb-2" data-testid="espace-tabs">
            {TABS.map(([key, label, Icon]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
                className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors whitespace-nowrap ${tab === key ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
                <Icon size={14} /> {label}
                {key === "certificats" && certs.length > 0 && <span className="bg-gold text-ink rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold px-1">{certs.length}</span>}
              </button>
            ))}
          </div>

          {loading ? <div className="text-center py-20"><Loader2 size={30} className="animate-spin text-gold mx-auto" /></div> : (
            <>
              {tab === "dashboard" && (
                <div data-testid="dashboard-tab">
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {[["Formations", stats.total], ["En cours", stats.started], ["Terminées", stats.done], ["Certificats", stats.certs], ["Progression", `${stats.global} %`]].map(([l, v]) => (
                      <Reveal key={l}><div className="rounded-sm border hairline bg-white p-5">
                        <div className="font-display text-3xl md:text-4xl font-semibold text-ink">{v}</div>
                        <div className="label-mono text-ink/45 mt-2">{l}</div>
                      </div></Reveal>
                    ))}
                  </div>
                  <div className="mt-6 rounded-sm border hairline bg-white p-6">
                    <div className="flex justify-between label-mono text-ink/50 mb-2"><span>Progression globale</span><span>{stats.global} %</span></div>
                    <div className="h-2.5 rounded-full bg-ink/5 overflow-hidden">
                      <motion.div className="h-full bg-gold rounded-full" animate={{ width: `${stats.global}%` }} transition={{ duration: 1 }} data-testid="global-progress-bar" />
                    </div>
                  </div>
                  {continueFormation && (
                    <Reveal delay={0.1}>
                      <div className="mt-6 rounded-sm border border-gold/40 bg-ink-900 text-paper p-7 grain relative overflow-hidden" data-testid="continue-card">
                        <div className="absolute right-0 top-0 w-40 h-40 rounded-full bg-gold/10 blur-[60px]" />
                        <span className="label-mono text-gold">Continuer ma formation</span>
                        <h3 className="font-display text-2xl md:text-3xl font-semibold mt-3 relative z-10">{continueFormation.title}</h3>
                        <div className="flex justify-between label-mono text-paper/50 mt-4 mb-2 relative z-10"><span>Progression</span><span>{continueFormation.percent} %</span></div>
                        <div className="h-2 rounded-full bg-paper/10 overflow-hidden relative z-10 max-w-md">
                          <div className="h-full bg-gold rounded-full" style={{ width: `${continueFormation.percent}%` }} />
                        </div>
                        <Link to={`/formation/${continueFormation.formation_lookup}/apprendre`} data-testid="continue-btn"
                          className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-3 label-mono hover:bg-paper transition-colors relative z-10">
                          Continuer <ArrowUpRight size={14} />
                        </Link>
                      </div>
                    </Reveal>
                  )}
                  {stats.total === 0 && (
                    <div className="mt-6 rounded-sm border hairline bg-white p-10 text-center" data-testid="empty-formations">
                      <GraduationCap size={40} className="text-ink/25 mx-auto" />
                      <h3 className="font-display text-2xl font-semibold mt-5">Aucune formation pour le moment</h3>
                      <p className="text-ink/55 mt-2">Explorez le catalogue pour commencer votre parcours NovaLean Learning.</p>
                      <Link to="/formation" className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-3 label-mono hover:bg-ink hover:text-paper transition-colors mt-6">Découvrir les formations <ArrowUpRight size={14} /></Link>
                    </div>
                  )}
                </div>
              )}

              {tab === "formations" && (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="formations-tab">
                  {progress.map((p) => {
                    const f = BY_LOOKUP[p.formation_lookup] || {};
                    const cert = certByLookup[p.formation_lookup];
                    const status = cert ? "reussie" : p.status;
                    return (
                      <Reveal key={p.formation_lookup}>
                        <article className="group rounded-sm border hairline bg-white overflow-hidden h-full flex flex-col" data-testid={`my-formation-${p.formation_lookup}`}>
                          {f.image && <div className="h-36 overflow-hidden"><img src={f.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>}
                          <div className="p-6 flex flex-col flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`label-mono px-2.5 py-1 rounded-full border ${STATUS_STYLE[status]}`}>{cert ? "Réussie" : STATUS_LABEL[p.status]}</span>
                              <span className="label-mono text-ink/40">{f.duration} · {f.level}</span>
                            </div>
                            <h3 className="font-display text-xl font-semibold mt-4 leading-tight">{p.title}</h3>
                            <p className="label-mono text-ink/40 mt-2">{p.done_modules}/{p.total_modules} modules terminés</p>
                            <div className="mt-4">
                              <div className="flex justify-between label-mono text-ink/45 mb-1.5"><span>Avancement</span><span>{p.percent} %</span></div>
                              <div className="h-2 rounded-full bg-ink/5 overflow-hidden"><div className="h-full bg-gold rounded-full transition-all" style={{ width: `${p.percent}%` }} /></div>
                            </div>
                            <div className="mt-auto pt-5">
                              {cert ? (
                                <a href={`${API}/certificates/${certs.find((c) => c.formation_lookup === p.formation_lookup)?.certificate_number}/pdf`} data-testid={`cert-dl-${p.formation_lookup}`}
                                  className="group/btn inline-flex items-center justify-between w-full label-mono text-gold hover:text-ink transition-colors">
                                  Certificat {cert.certificate_number} <Download size={16} />
                                </a>
                              ) : p.percent >= 100 ? (
                                <Link to={`/formation/${p.formation_lookup}/qcm`} data-testid={`qcm-${p.formation_lookup}`}
                                  className="group/btn inline-flex items-center justify-between w-full label-mono text-gold hover:text-ink transition-colors">
                                  Passer le QCM final <Award size={16} />
                                </Link>
                              ) : (
                                <Link to={`/formation/${p.formation_lookup}/apprendre`} data-testid={`learn-${p.formation_lookup}`}
                                  className="group/btn inline-flex items-center justify-between w-full label-mono text-ink hover:text-gold transition-colors">
                                  {p.percent > 0 ? "Continuer la formation" : "Commencer"} <ArrowUpRight size={16} className="group-hover/btn:rotate-45 transition-transform" />
                                </Link>
                              )}
                            </div>
                          </div>
                        </article>
                      </Reveal>
                    );
                  })}
                  {progress.length === 0 && <p className="text-ink/50 col-span-full">Aucune formation dans votre espace.</p>}
                </div>
              )}

              {tab === "resultats" && (
                <div className="rounded-sm border hairline bg-white overflow-hidden overflow-x-auto" data-testid="resultats-tab">
                  <table className="w-full text-left">
                    <thead className="label-mono text-ink/50 border-b hairline">
                      <tr><th className="px-6 py-4">Formation</th><th className="px-6 py-4">Date</th><th className="px-6 py-4">Score</th><th className="px-6 py-4">Seuil</th><th className="px-6 py-4">Statut</th><th className="px-6 py-4">Tentative</th><th className="px-6 py-4"></th></tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.id} className="border-b hairline last:border-0 hover:bg-paper/60 transition-colors" data-testid={`result-${r.id}`}>
                          <td className="px-6 py-4 font-semibold">{r.course_name}</td>
                          <td className="px-6 py-4 text-ink/60 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                          <td className="px-6 py-4 font-mono-tech font-semibold">{r.percent} %</td>
                          <td className="px-6 py-4 text-ink/60">{r.passing_score} %</td>
                          <td className="px-6 py-4">{r.passed
                            ? <span className="label-mono text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"><CheckCircle2 size={11} /> Réussi</span>
                            : <span className="label-mono text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">À repasser</span>}</td>
                          <td className="px-6 py-4 text-ink/60">n°{r.attempt_number}</td>
                          <td className="px-6 py-4">
                            {!r.passed && (
                              <Link to={`/formation/${r.formation_lookup}/qcm`} data-testid={`retry-${r.formation_lookup}`}
                                className="inline-flex items-center gap-1.5 label-mono text-gold hover:text-ink transition-colors whitespace-nowrap"><RotateCcw size={12} /> Repasser</Link>
                            )}
                          </td>
                        </tr>
                      ))}
                      {results.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-ink/50">Aucun QCM passé pour le moment.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "certificats" && (
                <div className="grid md:grid-cols-2 gap-5" data-testid="certificats-tab">
                  {certs.map((c) => (
                    <Reveal key={c.certificate_number}>
                      <article className={`rounded-sm border p-7 relative overflow-hidden ${c.status === "VALID" ? "border-gold/40 bg-ink-900 text-paper" : "hairline bg-white"}`} data-testid={`cert-card-${c.certificate_number}`}>
                        <div className="absolute right-0 top-0 w-32 h-32 rounded-full bg-gold/10 blur-[50px]" />
                        <div className="flex items-center gap-2 relative z-10">
                          <ShieldCheck size={18} className={c.status === "VALID" ? "text-green-400" : "text-red-400"} />
                          <span className={`label-mono ${c.status === "VALID" ? "text-green-400" : "text-red-500"}`}>{c.status === "VALID" ? "Certificat valide" : "Révoqué"}</span>
                        </div>
                        <h3 className="font-display text-2xl font-semibold mt-4 relative z-10">{c.course_name}</h3>
                        <div className={`mt-4 space-y-1.5 text-sm relative z-10 ${c.status === "VALID" ? "text-paper/60" : "text-ink/60"}`}>
                          <p>Obtenu le {c.completion_date} — Score : <strong className={c.status === "VALID" ? "text-paper" : "text-ink"}>{c.qcm_score} %</strong></p>
                          <p className="font-mono-tech">{c.certificate_number}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-6 relative z-10">
                          {c.status === "VALID" && (
                            <a href={`${API}/certificates/${c.certificate_number}/pdf`} data-testid={`cert-download-${c.certificate_number}`}
                              className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-5 py-2.5 label-mono hover:bg-paper transition-colors"><Download size={13} /> Télécharger PDF</a>
                          )}
                          <Link to={`/verify/${c.certificate_number}`} data-testid={`cert-verify-${c.certificate_number}`}
                            className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 label-mono transition-colors ${c.status === "VALID" ? "border-paper/30 hover:bg-paper hover:text-ink" : "hairline hover:bg-ink hover:text-paper"}`}>
                            <ShieldCheck size={13} /> Vérifier
                          </Link>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                  {certs.length === 0 && (
                    <div className="col-span-full rounded-sm border hairline bg-white p-10 text-center">
                      <Award size={40} className="text-ink/25 mx-auto" />
                      <p className="text-ink/55 mt-4">Terminez une formation à 100 % puis réussissez son QCM final pour obtenir votre premier certificat NovaLean.</p>
                    </div>
                  )}
                </div>
              )}

              {tab === "outils" && (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="outils-tab">
                  {tools.map((t) => (
                    <Reveal key={t.slug}>
                      <article className="group rounded-sm border hairline bg-white p-6 flex flex-col" data-testid={`my-tool-${t.slug}`}>
                        <span className="label-mono text-gold">{t.tool_type}</span>
                        <h3 className="font-display text-xl font-semibold mt-3 leading-tight">{t.name}</h3>
                        <div className="mt-auto pt-5">
                          {t.tool_type === "HTML" && (
                            <button onClick={() => viewTool(t)} data-testid={`view-tool-${t.slug}`} className="group inline-flex items-center justify-between w-full label-mono text-ink hover:text-gold transition-colors mb-2">
                              Ouvrir en ligne <ExternalLink size={15} />
                            </button>
                          )}
                          <button onClick={() => downloadTool(t)} data-testid={`download-tool-${t.slug}`} className="group inline-flex items-center justify-between w-full pt-4 border-t hairline label-mono text-ink hover:text-gold transition-colors">
                            Télécharger <Download size={16} className="group-hover:translate-y-0.5 transition-transform" />
                          </button>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                  {tools.length === 0 && (
                    <div className="col-span-full rounded-sm border hairline bg-white p-10 text-center">
                      <Wrench size={40} className="text-ink/25 mx-auto" />
                      <p className="text-ink/55 mt-4">Vos outils achetés apparaîtront ici.</p>
                      <Link to="/outils-apps" className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 label-mono hover:bg-gold hover:text-ink transition-colors mt-6">Explorer les outils</Link>
                    </div>
                  )}
                </div>
              )}

              {tab === "profil" && (
                <div className="max-w-xl rounded-sm border hairline bg-white p-8" data-testid="profil-tab">
                  <span className="label-mono text-gold">Mon profil</span>
                  <h3 className="font-display text-2xl font-semibold mt-3">{user?.name || "—"}</h3>
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="flex justify-between border-b hairline pb-3"><span className="label-mono text-ink/45">Email</span><span className="text-ink">{user?.email}</span></div>
                    <div className="flex justify-between border-b hairline pb-3"><span className="label-mono text-ink/45">Rôle</span><span className="text-ink capitalize">{user?.role}</span></div>
                    <div className="flex justify-between"><span className="label-mono text-ink/45">Membre depuis</span><span className="text-ink">{fmtDate(user?.created_at)}</span></div>
                  </div>
                  <div className="mt-8 pt-6 border-t hairline flex items-center gap-3 text-ink/50 text-sm">
                    <BookLock size={16} className="text-gold" /> Vos données restent privées et sécurisées.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
